import './styles/global.css';
import './components/UI/ui.css';

import { createLeaderboard } from './components/Leaderboard/Leaderboard.js';
import { createAdminBar } from './components/AdminPanel/AdminPanel.js';
import { createLoginModal } from './components/Auth/LoginModal.js';
import { showToast } from './components/UI/Toast.js';
import { authStore } from './store/auth.store.js';
import { leaderboardStore } from './store/leaderboard.store.js';
import { fetchMembers } from './api/leaderboard.js';
import { fetchActiveSeason } from './api/season.js';
import { subscribeToLeaderboard } from './utils/realtime.js';

// ── Layout ────────────────────────────────────────────────────────────────────

function createLayout(): void {
  const app = document.getElementById('app')!;

  app.innerHTML = `
    <div class="app-wrapper">
      <header class="app-header">
        <div class="app-container">
          <div class="app-logo">
            <span class="logo-mark">▲</span>
            <span class="logo-text">AMAZE</span>
          </div>
          <nav class="app-nav" id="app-nav"></nav>
        </div>
      </header>

      <main class="app-main">
        <div class="app-container">
          <div id="admin-bar-slot"></div>
          <div id="leaderboard-slot"></div>
        </div>
      </main>

      <footer class="app-footer">
        <div class="app-container">
          <span>Amaze Consortium &copy; ${new Date().getFullYear()}</span>
        </div>
      </footer>
    </div>
  `;

  injectLayoutStyles();
  mountNav();
  mountAdminBar();
  mountLeaderboard();
}

function mountNav(): void {
  const nav = document.getElementById('app-nav')!;

  const render = (): void => {
    const isAuth = authStore.isAuthenticated();

    nav.innerHTML = isAuth
      ? ''
      : `<button class="btn btn-ghost btn-sm" id="nav-login">Sign In</button>`;

    nav
      .querySelector('#nav-login')
      ?.addEventListener('click', () => createLoginModal().open());
  };

  render();
  authStore.subscribe(render);
}

function mountAdminBar(): void {
  const slot = document.getElementById('admin-bar-slot')!;
  slot.appendChild(createAdminBar());
}

function mountLeaderboard(): void {
  const slot = document.getElementById('leaderboard-slot')!;
  slot.appendChild(createLeaderboard());
}

// ── Data Bootstrap ────────────────────────────────────────────────────────────

async function loadInitialData(): Promise<void> {
  leaderboardStore.setLoading(true);

  try {
    const [seasonRes, membersRes] = await Promise.all([
      fetchActiveSeason(),
      fetchMembers(),
    ]);

    leaderboardStore.setSeason(seasonRes.season);
    leaderboardStore.setMembers(membersRes.members);

    // Subscribe to real-time updates for this season
    subscribeToLeaderboard(seasonRes.season.id);
  } catch (err: any) {
    leaderboardStore.setError(err.message ?? 'Failed to load leaderboard');
    showToast({ message: 'Failed to load data. Please refresh.', type: 'error' });
  }
}

// ── Boot ──────────────────────────────────────────────────────────────────────

function injectLayoutStyles(): void {
  if (document.getElementById('layout-styles')) return;

  const style = document.createElement('style');
  style.id = 'layout-styles';
  style.textContent = `
    .app-wrapper {
      min-height: 100dvh;
      display: flex;
      flex-direction: column;
    }

    .app-container {
      width: 100%;
      max-width: 720px;
      margin: 0 auto;
      padding: 0 20px;
    }

    .app-header {
      position: sticky;
      top: 0;
      z-index: 50;
      border-bottom: 1px solid var(--color-border);
      background: rgba(0,0,0,0.85);
      backdrop-filter: blur(12px);
    }

    .app-header .app-container {
      display: flex;
      align-items: center;
      justify-content: space-between;
      height: 56px;
    }

    .app-logo {
      display: flex;
      align-items: center;
      gap: 8px;
      font-weight: 800;
      letter-spacing: 0.08em;
    }

    .logo-mark {
      color: var(--color-accent);
      font-size: 1.1rem;
    }

    .logo-text {
      font-size: 0.9375rem;
      color: var(--color-text);
    }

    .app-nav {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .app-main {
      flex: 1;
      padding: 40px 0;
    }

    #admin-bar-slot:not(:empty) {
      margin-bottom: 24px;
    }

    .app-footer {
      border-top: 1px solid var(--color-border);
      padding: 20px 0;
    }

    .app-footer .app-container {
      display: flex;
      justify-content: center;
      font-size: 0.8125rem;
      color: var(--color-text-dim);
    }
  `;
  document.head.appendChild(style);
}

// Init
createLayout();
loadInitialData();
